<template lang="pug">
.main-container
  .filters(v-if="displayFilters")
    RogueSelect(
      v-if="filters.length > 2"
      name="tags"
      :options="filters"
      v-model="additional[filterType]"
      @update:modelValue="reload()"
    )
    RogueSelect(
      v-if="posts.length > 1"
      name="order"
      :options="[ { label: 'Newest', value: ''}, { label: 'Oldest', value: 'asc' }]"
      v-model="additional.order"
      @update:modelValue="reload()"
    )
  h2.underline(v-if="categoryName") {{ __(categoryName) }} {{selectedFilter === 'All' ? '' : selectedFilter}}
  .no-results-found(v-if='noResults && !loading && !selectedFilter')
    .empty-results-container
      p.bold
        | {{__(`We're sorry, we couldn't find any results for your search. Try your search again using these tips:`, 'categories')}}
      ul.bullet-points
        li {{__('Check your spelling', 'categories')}}
        li {{__('Try more general or similar words', 'categories')}}
        li {{__('Limit your search to one or two words', 'categories')}}
      p.bold
        | {{ __('If you still can\'t find what you are looking for,') }} &nbsp;
        rogue-link(to="/contact-us") {{ __('contact us.') }}
    FeaturedIndex(v-if="outsideIndexPage")
  IndexCard.featured-post( v-if="featured && showFeatured" :post="featured" )
  .grid
    IndexCard.card( v-for="post in posts" :post="post" :key="post.id")
    IndexCard.loading.card(v-if="loading" v-for="x in limit" :key="x")
  .show-more(v-show="!allLoaded && allowLoadMore && !noResults")
    RogueButton(
      color="white"
      :disabled="loading"
      @click="loadMore(false)"
    ) {{ __('Show More') }}
</template>

<script setup lang="ts">
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { deepEqual } from '../../../services/Utils'
import type { ITheIndexPost } from '../types'
import IndexCard from '@/js/pages/theindex/components/index-card.vue'
import { ALL_FIELDS, INDEX_FILTERS, loadPosts, loadTags, loadTagsByQuery, loadCategoriesByQuery } from '@/js/pages/theindex/helpers/search'
import { FEATURED_INDEX_PAGE_ID, INDEX_CATEGORIES, INDEX_CATEGORY_MAP } from '@/js/pages/theindex/data'
import { useHttpStore } from '@/js/stores/httpStore'
import { useRogue } from '@/js/services/Rogue'

type Query = {
  search?: string,
  categories?: number,
  tag?: number | null,
}

type CachedIndexData = {
  posts: ITheIndexPost[], 
  featured: ITheIndexPost | null | undefined, 
  noResults: boolean,
};

const props = withDefaults(defineProps<{
  query: Query,
  limit?: number,
  showFilters?: boolean,
  allowLoadMore?: boolean,
  showFeatured?: boolean,
  ssr?: boolean,
  categoryName?: string,
  indexSearchResults?: boolean,
}>(), {
  limit              : 9,
  showFilters        : true,
  allowLoadMore      : true,
  showFeatured       : false,
  ssr                : false,
  indexSearchResults : false,
})

const emit = defineEmits(['indexPostsCount'])

const httpStore = useHttpStore()
const rogue = useRogue()
const route = useRoute()
const cachedData = (props.ssr && !rogue.ssr)
  ? (httpStore.cache[`index-${JSON.stringify(props.query)}`] as CachedIndexData)
  : {noResults: false}

const posts = ref<ITheIndexPost[]>([])
const filters = ref<{
  label: string,
  value: string | number,
}[]>([])
const page = ref<number>(0)
const additional = ref({
  categories : '',
  order      : '',
  tags       : '',
})
const allLoaded = ref<boolean>(false)
const error = ref<boolean>(false)
const noResults = ref<boolean>(cachedData?.noResults || false)
const loading = ref<boolean>(!noResults.value)
const featured = ref<ITheIndexPost | null | undefined>(null)
const filtersFullyHydrated = ref(false)

const filterType = computed<'tags' | 'categories'>(() => props.query.categories ? 'tags' : 'categories')
const outsideIndexPage = computed<boolean>(() => !route.path.startsWith('/theindex'))
const selectedFilter = computed<string>(() => {
  const selected = filters.value?.find(filter => filter.value === additional.value[filterType.value])?.label || additional.value[filterType.value] 
  
  return selected && selected !== 'All' ? '| ' + selected : ''
})

const displayFilters = computed<boolean>(() => 
  Boolean(props.showFilters && (!noResults.value || selectedFilter.value))
)

const showTagsSelect = computed(() => {
  // Only show the tags select if the data is ready AND we have enough options
  return filtersFullyHydrated.value && filters.value.length > 2
})

watch(() => props.query, (newVal, oldVal) => {
  if (deepEqual(newVal, oldVal)) {
    return
  }
  additional.value = { categories: '', order: '', tags: '' }
  getFilters()
  reload()
}, {
  deep: true,
})

onServerPrefetch(async () => {
  if (props.ssr) {
    await loadMore()
    httpStore.setCache(`index-${JSON.stringify(props.query)}`, { posts: posts.value, featured: featured.value, noResults: noResults.value })
  }
  else{
    loading.value = true
  }
})

if (props.ssr && !rogue.ssr) {
  const { posts: cachedPosts, featured: cachedFeatured } = httpStore.cache[`index-${JSON.stringify(props.query)}`] as { posts: ITheIndexPost[], featured: ITheIndexPost }
    || {} as Partial<{ posts: ITheIndexPost[], featured: ITheIndexPost }>
  if (cachedPosts) {
    posts.value = cachedPosts
    featured.value = cachedFeatured
    page.value = 1
    loading.value = false
    emit('indexPostsCount', posts.value.length)
  }
}

onMounted(async () => {
  
  await getFilters()
  if (!posts.value.length && !noResults.value) {
    await loadMore()
  }
  
  else if (!posts.value.length && page.value > 0){
    noResults.value = true
    allLoaded.value = true
  }
  else{
    allLoaded.value = posts.value.length < props.limit
  }

  filtersFullyHydrated.value = true
})

async function getFilters(): Promise<void> {
  if (props.showFilters) {
    const all = { label: 'All', value: '' }
    if (filterType.value === 'tags') {
      await loadTags(rogue)
      const catName = INDEX_CATEGORY_MAP[props.query.categories!].slug
      const catFilters = INDEX_FILTERS[catName!] || []
      if(props.query.search && props.query.categories && catFilters) {
        const customizedFilters = await loadTagsByQuery(rogue, props.query.search, props.query.categories, catFilters)
        filters.value = [all, ...customizedFilters]
      }else{
        filters.value = [all, ...catFilters]
      }
    } else if(props.query.search && props.indexSearchResults ) {

      const customizedCatFilters = await loadCategoriesByQuery(rogue, props.query.search)
      filters.value = [all, ...customizedCatFilters.filter(c => c.slug).map(c => ({
        label: c.name, value: c.id,
      }))]
    }
    else{
      filters.value = [all, ...INDEX_CATEGORIES.filter(c => c.slug).map(c => ({
        label: c.name, value: c.id,
      }))]
    }
    
  }
}

function reload(): void {
  page.value = 0
  posts.value = []
  loadMore(true)
}

async function loadMore(reloading = false): Promise<void> {
  loading.value = true
  const query = JSON.parse(JSON.stringify({ ...additional.value, ...props.query }))
  Object.keys(query).filter(key => query[key] === '').forEach(key => delete query[key])

  try {
    let newResults = await loadPosts(rogue, query, {
      page        : page.value + 1,
      perPage     : page.value === 0 && props.showFeatured ? Math.max(20, props.limit) : props.limit,
      withHeaders : true,
    })

    if (page.value === 0 && props.showFeatured) {
      const total = newResults.totalCount
      const newFeatured = newResults.find(p => p.categories.includes(FEATURED_INDEX_PAGE_ID))
      newResults = newResults.filter(p => p !== newFeatured).slice(0, props.limit)
      featured.value = newFeatured
      if (total) {
        newResults.totalCount = total
      }
      if (newFeatured) {
        const [fullData] = await loadPosts(rogue, { include: newFeatured.id }, { fields: ALL_FIELDS })

        if (fullData) {
          Object.assign(featured.value as object, fullData)
        }
      }
    }

    if (page.value <= 1 && newResults.length === 0) {
      noResults.value = true
    }
    else {
      noResults.value = false
    }

    page.value += 1
    allLoaded.value = newResults.length < props.limit || (newResults.totalCount ?? 0) <= props.limit 
    if(reloading) {
      posts.value = newResults
    }
    else{
      posts.value = posts.value.concat(newResults)
    }
  } catch (e) {
    allLoaded.value = true
    error.value = true
    noResults.value = (page.value === 0)
  }
  emit('indexPostsCount', posts.value.length)
  loading.value = false
}
</script>

<style lang="scss" scoped>
.filters {
  margin-bottom: $space-s;
  display: flex;
  flex-wrap: nowrap;
  column-gap: $space-s;

  @include mobile() {
    margin-left: -$space-s;
    margin-right: -$space-s;
    column-gap: $space-xxs;
  }
}

.card {
  text-decoration: none;
}

.grid {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -#{$space-xs};
}

@include mobile() {
  .no-results-found{
    margin-bottom: $size-xxxl;
  }
   :deep() {
    .contents {
      padding-bottom: $size-xxxxl;
    }
  }
}

.featured-post {
  margin-bottom: $space-xxl;

  :deep() {
    .rg-img::after {
      padding-top: 56.25%;
    }
  }
}

.card {
  flex-basis: 100%;
  margin-bottom: $space-xxl;
  padding: $space-xs;

  @media(min-width: $tablet) {
    flex-basis: 50%
  }

  @media(min-width: $desktop) {
    flex-basis: 33.333%;
  }
}

.field {
  display: inline-block;
  margin-bottom: $space-s;

  &:not(:last-child) {
    margin-right: $space-s;
  }
}

.show-more {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $space-l;

  &:before, &:after {
    content: '';
    background: $color-gray-light;
    height: 1px;
    flex-grow: 2;
  }

  .button {
    margin: 0 $space-l;
  }

}

.empty-results-container {
  padding-top: $space-m;
  padding-bottom: $space-l;
  @include not-mobile() {
    padding-left: $space-l;
  }
}

.bullet-points{
  margin-bottom: $space-s;
  padding-left: $space-m;
}
</style>
